import type { SxProps, Theme } from "@mui/material";
import { Grid } from "@mui/material";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConditionFilterDescriptor } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import type { ShowFieldOptionsSettings } from "../Types.ts";
import { getFiltersToApply } from "../hooks/getFiltersToApply.ts";
import ConditionFieldOptionPopup from "./ConditionFieldOptionPopup.tsx";
import ConditionItemContainer from "./ConditionItemContainer.tsx";

interface Props {
  conditions: ConditionField[];
  canBeRemoved: boolean;
  readonly?: boolean;
  disableEqualitySelection?: boolean;
  sx?: SxProps<Theme>;
  renderCustomId?: (field: ConditionField) => JSX.Element;
  onRemoveFields?: (field: ConditionField) => void;
  onFieldChanged: (field: ConditionField, changes: Partial<ConditionField>) => void;
}

export const Conditions = ({
  conditions,
  canBeRemoved,
  readonly,
  onRemoveFields,
  onFieldChanged,
  disableEqualitySelection,
  renderCustomId,
  sx,
}: Props) => {
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [optionField, setOptionField] = useState<ConditionField>();
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const narrowingFilters = useMemo(
    () => (optionField === undefined ? [] : getFiltersToApply(conditions, optionField)),
    [conditions, optionField]
  );

  useEffect(() => {
    if (optionField !== undefined) {
      const field = conditions.find((f) => f.config.guid === optionField.config.guid);
      if (field !== undefined) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptionField(field);
      }
    }
  }, [conditions, optionField]);

  const handleClosePopup = useCallback(() => {
    setOptionsAnchorEl(null);
  }, []);

  const onShowOptions = useCallback((settings: ShowFieldOptionsSettings<ConditionField>) => {
    setOptionField(settings.field);
    setOptionsAnchorEl(settings.ref);
    setShowOnlySelected(settings.showOnlySelected);
  }, []);

  const onApplyFilter = useCallback(
    (filter: ConditionFilterDescriptor) => {
      if (optionField) {
        onFieldChanged(optionField, { config: { ...optionField.config, filter } });
      }
      handleClosePopup();
    },
    [optionField, onFieldChanged, handleClosePopup]
  );

  return (
    <Grid container sx={{ gap: 1, width: "100%", ...sx }}>
      {conditions.map((f) => {
        return (
          <ConditionItemContainer
            key={f.config.guid}
            field={f}
            canBeRemoved={canBeRemoved}
            readonly={readonly}
            onRemoveItem={onRemoveFields}
            showOptions={onShowOptions}
            disableEqualitySelection={disableEqualitySelection}
          />
        );
      })}
      {optionsAnchorEl && optionField && (
        <ConditionFieldOptionPopup
          anchorEl={optionsAnchorEl}
          field={optionField}
          narrowingFilters={narrowingFilters}
          showOnlySelectedByDefault={showOnlySelected}
          canBeRemoved={canBeRemoved}
          readonly={readonly}
          save={onApplyFilter}
          resizable={optionField.meta.type !== ItemDataType.Date}
          readonlyCustomLabel={true}
          onRemoveItem={() => {
            if (!onRemoveFields) return;
            onRemoveFields(optionField);
            handleClosePopup();
          }}
          disableEqualitySelection={disableEqualitySelection}
          CustomId={renderCustomId?.(optionField)}
        />
      )}
    </Grid>
  );
};

export default Conditions;
